import { defineComponent, h, ref, watch } from 'vue';
import { Modal, modalProps, ModalProps } from '@/ui/components/Modal';
import { button, loader } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { ActivitiesList } from '@/entities/activity/model';
import { emptyList } from '@/services/api';
import { ActivityQuery } from '@/services/api/resources/activity/query';
import { useTask } from '@/ui/concerns';
import { listActivities, LogType } from '@/services/api/resources/activity';
import { ActivityLogs } from '@/ui/components/activityLogs/ActivityLogs';
import { LogCreator } from '@/ui/components/activityLogs/factory/LogCreator';

interface ActivityModalProps extends ModalProps {
  id: number;
  logType: LogType;
  title: string;
}

export const ActivityModal = defineComponent<ActivityModalProps>({
  name: 'ActivityModal',
  props: [...modalProps, 'id', 'logType', 'title'],
  setup(props, context) {
    const closeModal = () => {
      context.emit('close');
    };

    const logs = ref<ActivitiesList>(emptyList());
    const isLoadingActivities = ref(false);

    const query = ref<ActivityQuery>({
      page: 1,
      perPage: 100,
    });

    const fetchActivities = useTask(
      () => {
        isLoadingActivities.value = true;
        return listActivities(props.id, props.logType, query.value);
      },
      response => {
        logs.value.meta = response.meta;
        logs.value.links = response.links;
        logs.value.data = response.data;
        isLoadingActivities.value = false;
      },
    );

    watch(ref(props.id), fetchActivities.run);

    return function render() {
      return h(Modal, {
        props: {
          ...props,
          width: '625px',
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
        },
        class: 'activity-modal',
        on: {
          close: closeModal,
        },
        scopedSlots: {
          actions: () => h('div', [
            button(t('common.close'), closeModal),
          ]),
        },
      }, [
        loader('div', fetchActivities, [
          h('h2', { class: 'title' }, props.title ?? t('inspections.inspection-log')),
          !isLoadingActivities.value
            && h(ActivityLogs, {
              props: {
                logs: logs.value.data,
                showCreateComment: false,
                resourceName: t(`common.${props.logType}`).toLowerCase(),
                componentCreator: new LogCreator(),
                customClasses: ['activity-logs__box--no-borders'],
              },
            }),
        ]),
      ]);
    };
  },
});
